import { useState, useCallback, useRef } from 'react'

export interface ColumnWidths {
  [key: string]: number | undefined
}

interface UseColumnResizeOptions {
  defaultWidths?: ColumnWidths
  minWidth?: number
}

/**
 * Hook for resizable table columns.
 * Returns column widths, a resize start handler, and a resize handler.
 */
export function useColumnResize(options: UseColumnResizeOptions = {}) {
  const { defaultWidths = {}, minWidth = 60 } = options
  const [columnWidths, setColumnWidths] = useState<ColumnWidths>(defaultWidths)
  const resizeState = useRef<{
    column: string
    startX: number
    startWidth: number
  } | null>(null)

  const handleResizeStart = useCallback(
    (column: string, e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      const currentWidth = columnWidths[column]
      const th = (e.target as HTMLElement).closest('th')
      const startWidth = currentWidth ?? th?.offsetWidth ?? 100
      resizeState.current = { column, startX: e.clientX, startWidth }
    },
    [columnWidths],
  )

  const handleResizeMove = useCallback(
    (e: MouseEvent) => {
      if (!resizeState.current) return
      const { column, startX, startWidth } = resizeState.current
      const delta = e.clientX - startX
      const newWidth = Math.max(minWidth, startWidth + delta)
      setColumnWidths((prev) => ({ ...prev, [column]: newWidth }))
    },
    [minWidth],
  )

  const handleResizeEnd = useCallback(() => {
    resizeState.current = null
  }, [])

  // Helper to get width for a column
  const getColumnStyle = useCallback(
    (column: string): React.CSSProperties => {
      const w = columnWidths[column]
      return w ? { width: w, minWidth: w } : {}
    },
    [columnWidths],
  )

  return {
    columnWidths,
    setColumnWidths,
    handleResizeStart,
    handleResizeMove,
    handleResizeEnd,
    getColumnStyle,
  }
}
